'use client'

import { useState } from 'react'
import { useIntl } from 'react-intl'
import { InfoCircle16, X24 } from '@vinted/monochrome-icons'
import {
  BottomSheet,
  Button,
  Cell,
  Dialog,
  Divider,
  Icon,
  Navigation,
  Spacer,
  Text,
} from '@vinted/web-ui'

import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import {
  ComplaintEstimatedReleaseModel,
  formatCurrencyAmount,
} from '@marketplace-web/complaints/complaints-data'

type Props = {
  isOpen: boolean
  isBundleOrder: boolean
  isOpenedInDialog?: boolean
  refundEstimate: ComplaintEstimatedReleaseModel | null
  onClose: () => void
}

const TotalRefundModal = ({
  isOpen,
  refundEstimate,
  isOpenedInDialog = false,
  onClose,
  isBundleOrder,
}: Props) => {
  const { locale } = useIntl()
  const translate = useTranslate('total_refund_modal')
  const { phones } = useBreakpoint()
  const isMobileView = phones && !isOpenedInDialog

  const discountsInfoBodyTranslationKey = isBundleOrder
    ? 'discounts_info'
    : 'single_item_discounts_info'

  const [showDiscountInfo, setShowDiscountInfo] = useState<boolean>(false)

  const handleModalClose = () => onClose()

  const renderItems = () => {
    if (!refundEstimate?.details?.items) return []

    return refundEstimate.details.items.map((item, index) => (
      <Cell
        styling="tight"
        key={`${item.title}-${index}`}
        body={item.title}
        suffix={formatCurrencyAmount(item.original, locale)}
      />
    ))
  }

  const handleInfoClick = () => setShowDiscountInfo(true)

  const handleDiscountsInfoClose = () => setShowDiscountInfo(false)

  const handleCloseDialogClick = () =>
    showDiscountInfo ? handleDiscountsInfoClose() : handleModalClose()

  const renderTotalDiscount = () => {
    if (!refundEstimate?.details.totalItemsDiscount) return null

    return (
      <>
        <Spacer size="large" />
        <Cell
          styling="tight"
          body={
            <div className="u-flexbox">
              {translate('order_discount')}
              <Spacer orientation="vertical" />
              <button
                className="u-flexbox u-align-items-center"
                onClick={handleInfoClick}
                type="button"
                aria-label={translate('a11y.discounts_info_icon')}
              >
                <Icon name={InfoCircle16} color="greyscale-level-2" testId="discounts-info-icon" />
              </button>
            </div>
          }
          suffix={formatCurrencyAmount(refundEstimate.details.totalItemsDiscount, locale, {
            formatAsNegative: true,
          })}
        />
      </>
    )
  }

  const renderShippingVat = () => {
    if (!refundEstimate?.details.refundableShippingVat) return null

    return (
      <>
        <Spacer size="large" />
        <Cell
          styling="tight"
          body={<Text as="span" text={translate('shipping_vat')} />}
          suffix={formatCurrencyAmount(refundEstimate.details.refundableShippingVat, locale)}
        />
      </>
    )
  }

  const renderTotalRefund = () => {
    if (!refundEstimate) return null

    return (
      <>
        <Spacer size="large" />
        <Cell
          styling="tight"
          prefix={<Text as="h2" type="title" text={translate('total_refund')} />}
          suffix={
            <Text
              as="h2"
              type="title"
              text={formatCurrencyAmount(refundEstimate.totalEstimatedRefund, locale)}
            />
          }
        />
      </>
    )
  }

  const renderPricingInfo = () => {
    if (showDiscountInfo)
      return (
        <Cell>
          <Text as="span" text={translate(discountsInfoBodyTranslationKey)} />
        </Cell>
      )

    if (!refundEstimate) return null

    return (
      <Cell>
        <Cell styling="tight">
          <SeparatedList separator={<Spacer size="small" />}>{renderItems()}</SeparatedList>
        </Cell>
        {renderTotalDiscount()}
        {renderShippingVat()}
        {renderTotalRefund()}
      </Cell>
    )
  }

  const renderContent = () => {
    return (
      <div className="u-fill-width">
        <Navigation
          body={showDiscountInfo ? translate('discounts_title') : translate('total_refund')}
          right={
            <Button
              inline
              iconName={X24}
              styling="flat"
              testId="total-refund-modal-close"
              aria={{ 'aria-label': translate('a11y.close') }}
              onClick={handleCloseDialogClick}
            />
          }
        />
        <Divider />
        <ScrollableArea>
          {renderPricingInfo()}
          {!isMobileView && (
            <Cell>
              <Button text={translate('close')} styling="filled" onClick={handleCloseDialogClick} />
            </Cell>
          )}
        </ScrollableArea>
      </div>
    )
  }

  if (!refundEstimate) return null

  if (isMobileView) {
    return (
      <BottomSheet isVisible={isOpen} onClose={handleCloseDialogClick}>
        {renderContent()}
      </BottomSheet>
    )
  }

  return (
    <Dialog
      hasScrollableContent
      show={isOpen}
      defaultCallback={handleCloseDialogClick}
      closeOnOverlay
    >
      {renderContent()}
    </Dialog>
  )
}

export default TotalRefundModal
